import { ATTACHMENT_ACCEPT, type AssistantAttachment } from '@secretario/shared';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { attachmentUrl, type PendingAttachment } from '../../lib/assistantAttachments.js';
import { hapticTap } from '../../lib/native.js';
import { IconCamera, IconClip } from '../chrome/icons.js';
import { GlyphClip, GlyphImage } from '../ui/Glyphs.js';
import '../messaging/composer-extras.css';
import './attachments.css';

/**
 * Clipe da conversa com o Secretário: Câmera, Fotos e Arquivo, pelo seletor do sistema
 * (nenhuma permissão de fotos ou arquivos). `onPickStart`/`onPickEnd` avisam quem está
 * ouvindo: o ditado do Android para quando o seletor abre e volta depois.
 */
export function AttachPicker({
  disabled = false,
  onFiles,
  onPickStart,
  onPickEnd,
  className = 'th-pill-btn',
  up = true,
}: {
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  onPickStart?: () => void;
  onPickEnd?: (picked: number) => void;
  className?: string;
  /** O menu abre para cima (compositor no pé da tela) ou para baixo (cabeçalho). */
  up?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const photosRef = useRef<HTMLInputElement | null>(null);
  const filesRef = useRef<HTMLInputElement | null>(null);
  const picking = useRef(false);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('click', close, true);
    return () => window.removeEventListener('click', close, true);
  }, [open]);

  // Seletor fechado sem escolher: alguns navegadores não avisam "cancel"; a volta do foco avisa.
  useEffect(() => {
    const back = () => {
      if (!picking.current) return;
      setTimeout(() => {
        if (!picking.current) return;
        picking.current = false;
        onPickEnd?.(0);
      }, 700);
    };
    window.addEventListener('focus', back);
    return () => window.removeEventListener('focus', back);
  }, [onPickEnd]);

  function choose(input: HTMLInputElement | null) {
    setOpen(false);
    if (!input) return;
    picking.current = true;
    onPickStart?.();
    input.click();
  }

  function picked(input: HTMLInputElement) {
    const files = Array.from(input.files ?? []);
    input.value = '';
    picking.current = false;
    if (files.length > 0) onFiles(files);
    onPickEnd?.(files.length);
  }

  const inputProps = (multiple: boolean) => ({
    type: 'file' as const,
    hidden: true,
    multiple,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => picked(event.currentTarget),
    onCancel: () => {
      picking.current = false;
      onPickEnd?.(0);
    },
  });

  return (
    <span className={`cx-attach at-picker${up ? '' : ' down'}`} ref={rootRef}>
      <button
        type="button"
        className={`${className}${open ? ' on' : ''}`}
        aria-label="Anexar foto ou arquivo"
        aria-expanded={open}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          hapticTap();
          setOpen((value) => !value);
        }}
      >
        <IconClip />
      </button>
      {open && (
        <div className="cx-menu at-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => choose(cameraRef.current)}>
            <IconCamera /> Câmera
          </button>
          <button type="button" role="menuitem" onClick={() => choose(photosRef.current)}>
            <GlyphImage /> Fotos
          </button>
          <button type="button" role="menuitem" onClick={() => choose(filesRef.current)}>
            <GlyphClip /> Arquivo
          </button>
          <small className="at-menu-note">PDF, Word, Excel, PowerPoint e texto</small>
        </div>
      )}
      <input ref={cameraRef} {...inputProps(false)} accept="image/*" capture="environment" />
      <input ref={photosRef} {...inputProps(true)} accept="image/*" />
      <input ref={filesRef} {...inputProps(true)} accept={ATTACHMENT_ACCEPT} />
    </span>
  );
}

const KIND_LABEL: Record<AssistantAttachment['kind'], string> = {
  image: 'Foto',
  pdf: 'PDF',
  text: 'Arquivo',
};

function extensionOf(name: string): string {
  return (name.match(/\.([a-z0-9]{2,5})$/i)?.[1] ?? '').toUpperCase();
}

function FileGlyph({ kind, name }: { kind: AssistantAttachment['kind']; name: string }) {
  const ext = kind === 'pdf' ? 'PDF' : extensionOf(name) || KIND_LABEL[kind];
  const tone = /XLS|CSV/.test(ext)
    ? 'sheet'
    : /DOC/.test(ext)
      ? 'doc'
      : ext === 'PDF'
        ? 'pdf'
        : /PPT/.test(ext)
          ? 'slide'
          : 'txt';
  return (
    <span className={`at-file-glyph ${tone}`} aria-hidden="true">
      <svg viewBox="0 0 24 30" width="24" height="30">
        <path
          d="M3 2.5h12l6 6V27a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 27V2.5Z"
          className="sheet"
        />
        <path d="M15 2.5v6h6" className="fold" />
      </svg>
      <b>{ext.slice(0, 4)}</b>
    </span>
  );
}

/** Bandeja acima do campo: o que vai junto, com o × para tirar e o estado de cada um. */
export function AttachTray({
  items,
  notice,
  onRemove,
}: {
  items: PendingAttachment[];
  notice: string | null;
  onRemove: (key: string) => void;
}) {
  if (items.length === 0 && !notice) return null;
  const failed = items.find((item) => item.state === 'failed');
  return (
    <div className="at-tray" aria-label="Anexos desta mensagem">
      {items.length > 0 && (
        <ul className="at-tray-row">
          {items.map((item) => (
            <li key={item.key} className={`at-chip ${item.kind} ${item.state}`}>
              {item.preview ? (
                <img src={item.preview} alt="" />
              ) : (
                <span className="at-chip-file">
                  <FileGlyph kind={item.kind} name={item.name} />
                  <span className="at-chip-name">{item.name}</span>
                </span>
              )}
              {item.state === 'uploading' && <span className="at-spin" aria-label="Enviando" />}
              <button
                type="button"
                className="at-x"
                aria-label={`Tirar ${item.name}`}
                onClick={() => onRemove(item.key)}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path
                    d="M7 7l10 10M17 7 7 17"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
      {(notice || failed?.error) && (
        <p className="at-tray-note" role="status">
          {notice ?? failed?.error}
        </p>
      )}
    </div>
  );
}

function Thumb({ item, onOpen }: { item: AssistantAttachment; onOpen: (url: string) => void }) {
  const [url, setUrl] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void attachmentUrl(item.id).then((found) => {
      if (alive) setUrl(found);
    });
    return () => {
      alive = false;
    };
  }, [item.id]);
  if (url === null) return <span className="at-thumb gone">Foto de mais de 30 dias</span>;
  return (
    <button
      type="button"
      className={`at-thumb${url ? '' : ' loading'}`}
      aria-label={`Ver ${item.name}`}
      onClick={(event) => {
        event.stopPropagation();
        if (url) onOpen(url);
      }}
    >
      {url && <img src={url} alt={item.name} />}
    </button>
  );
}

/** O que o dono mandou, dentro do balão dele: fotos em grade e arquivos em fichas. */
export function TurnAttachments({ attachments }: { attachments: AssistantAttachment[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const images = attachments.filter((item) => item.kind === 'image');
  const files = attachments.filter((item) => item.kind !== 'image');
  return (
    <div className="at-turn">
      {images.length > 0 && (
        <div className={`at-grid n${Math.min(images.length, 4)}`}>
          {images.map((item) => (
            <Thumb key={item.id} item={item} onOpen={setOpen} />
          ))}
        </div>
      )}
      {files.map((item) => (
        <span key={item.id} className="at-filecard">
          <FileGlyph kind={item.kind} name={item.name} />
          <span className="at-filecard-text">
            <b>{item.name}</b>
            <small>{sizeLabel(item.size)}</small>
          </span>
        </span>
      ))}
      {open &&
        createPortal(
          <div className="at-view" role="dialog" aria-label="Foto" onClick={() => setOpen(null)}>
            <img src={open} alt="" />
            <button type="button" className="at-view-x" aria-label="Fechar">
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6 6 18"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}
